import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Button from '../components/Button'
import { useSchoolStore } from '../../stores/schoolsStore'
import Icon from '../components/Icon'
import { useAuth } from '../../contexts/AuthContext'
import { CreateSchoolRequest } from '../../services/schools'

const SchoolEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    schools,
    loading,
    error,
    isCreating,
    isUpdating,
    fetchSchools,
    addSchool,
    editSchool,
    removeSchool,
    clearError,
  } = useSchoolStore()

  const [schoolDisplayName, setSchoolDisplayName] = useState('')
  const [importNames, setImportNames] = useState<string[]>([''])

  const isNewSchool = id === 'new'
  const currentSchool = !isNewSchool
    ? schools.find((school) => school._id === id)
    : null

  useEffect(() => {
    if (schools.length === 0) {
      fetchSchools()
    }
  }, [schools.length, fetchSchools])

  useEffect(() => {
    if (!isNewSchool && currentSchool) {
      setSchoolDisplayName(currentSchool.displayName)
      setImportNames(
        Array.isArray(currentSchool.importNames) &&
          currentSchool.importNames.length > 0
          ? currentSchool.importNames
          : [''],
      )
    }
  }, [isNewSchool, currentSchool])

  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  const handleSaveSchool = async () => {
    const organizationId =
      typeof user?.organization === 'string'
        ? user.organization
        : user?.organization._id

    if (!schoolDisplayName.trim() || !organizationId) {
      return
    }

    try {
      const cleanImportNames = importNames.filter(
        (name: string) => name.trim() !== '',
      )

      const schoolData: CreateSchoolRequest = {
        displayName: schoolDisplayName.trim(),
        importNames: cleanImportNames,
        organization: organizationId,
      }

      if (isNewSchool) {
        await addSchool(schoolData)
      } else if (id) {
        await editSchool(id, schoolData)
      }

      navigate('/manage/schools')
    } catch (err) {
      console.error(
        isNewSchool ? 'Error creating school:' : 'Error updating school:',
        err,
      )
    }
  }

  const handleDeleteSchool = async () => {
    if (!currentSchool) return

    if (
      window.confirm(
        `Are you sure you want to delete "${currentSchool.displayName}"? This action cannot be undone.`,
      )
    ) {
      try {
        await removeSchool(currentSchool._id)
        navigate('/manage/schools')
      } catch (err) {
        console.error('Error deleting school:', err)
      }
    }
  }

  const handleImportNameChange = (index: number, value: string) => {
    const newImportNames = [...importNames]
    newImportNames[index] = value
    setImportNames(newImportNames)
  }

  const addImportNameField = () => {
    setImportNames([...importNames, ''])
  }

  const removeImportNameField = (index: number) => {
    if (importNames.length > 1) {
      const newImportNames = importNames.filter(
        (_: any, i: number) => i !== index,
      )
      setImportNames(newImportNames)
    }
  }

  if (loading && schools.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isNewSchool && !currentSchool && !loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          School not found
        </div>
        <Button
          onClick={() => navigate('/manage/schools')}
          variant="link"
          className="mt-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Schools
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto space-y-6">
      {/* Header with Back Button */}
      <div>
        <Button
          onClick={() => navigate('/manage/schools')}
          variant="link"
          className="mb-4 flex items-center gap-2"
        >
          <Icon type="arrow-left" className="w-5 h-5" />
          Back to Schools
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNewSchool ? 'Create New School' : 'Edit School'}
        </h1>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Name
          </label>
          <input
            type="text"
            value={schoolDisplayName}
            onChange={(e) => setSchoolDisplayName(e.target.value)}
            placeholder="Enter school display name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500">
            This is the name that will be displayed throughout the application
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Import Names (Optional)
          </label>
          <div className="space-y-2">
            {importNames.map((name: string, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) =>
                    handleImportNameChange(index, e.target.value)
                  }
                  placeholder="Enter import alias"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Button
                  type="button"
                  onClick={() => removeImportNameField(index)}
                  disabled={importNames.length === 1}
                  variant="ghost"
                  className="p-2 text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <Icon type="x" className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              onClick={addImportNameField}
              variant="link"
              className="text-sm flex items-center gap-1"
            >
              <Icon type="plus" className="w-4 h-4" />
              Add Import Name
            </Button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Alternative names that can be used when importing student data from
            CSV files
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <div>
            {!isNewSchool && (
              <Button
                onClick={handleDeleteSchool}
                disabled={isUpdating}
                variant="link"
                className="px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"
              >
                Delete School
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/manage/schools')}
              disabled={isCreating || isUpdating}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSchool}
              disabled={isCreating || isUpdating || !schoolDisplayName.trim()}
              variant="primary"
              loading={isCreating || isUpdating}
            >
              {isNewSchool ? 'Create School' : 'Update School'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SchoolEdit
